<template>
  <div
    style="display: flex; flex-direction: column; height: 100%; flex: 1;"
  >
    <editor-content
      :editor="editor"
      class="editor flex-item"
    />
    <div
      class="message-cost-text"
    >
      <span
        class="grey-icon"
        style="margin: auto 0 auto 1em; float: left; font-weight: 600; cursor: pointer;"
        @click="openMsgCalcNoteModal"
      >?</span>
      <span
        style="margin: auto 0 auto 0.5em; float: left;"
      > 
        <!-- TODO: @dineshnagumothusr handle differing cost of messages (e.g. 0.12c vs 0.04p per message) through i18n -->
        <span
          v-if="messageType === 'sms' || messageType === 'both'"
        >
          <strong>{{ (perMessageCostFraction * messageParts) }}{{ $t('currencyFractionSymbol') }} </strong> per message
        </span>
      </span>
      <ProgressCircle
        v-if="messageType === 'sms' || messageType === 'both'"
        style="margin: 0 1em 0 auto; float:right;"
        :chars="messageLength"
        :max-chars="maxChars*messageParts"
      />
    </div>  
  </div>
  <WarningMessageModal
    v-if="displayWarningMessageModal"
    :message="warningMessageBody"
    @close-modal="displayWarningMessageModal = false"
  />
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Highlight from '@tiptap/extension-highlight'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import { markPasteRule } from '@tiptap/core'
import HardBreak from '@tiptap/extension-hard-break'
import ProgressCircle from '@/components/progress/progress-circle.vue'
import History from '@tiptap/extension-history'
import WarningMessageModal from '@/components/messages/warning-message-modal.vue'

export default {
  name: 'TextEditor',
  components: {
    EditorContent,
    ProgressCircle,
    WarningMessageModal
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: true
    },
    messageType: {
      type: String,
      default: ''
    },
     /**
     * Message max chars
     */
     maxChars: {
      type: Number,
      default: 1,
    },
    /**
     * Message length
     */
    messageLength: {
      type: Number,
      default: 0
    },
  },
  emits: [
    'update:modelValue',
    'openMsgCalcNote'
  ],
  data() {
    return {
      editor: null,
      // Regex to apply on highlighting
      nameHighlightRegex: /{First Name}|{Last Name}|{first_name}|{last_name}|{consentId}|{Consent Id}|{Consent ID}|{consent_id}/g,
      pharmacyHighlightRegex: /{Pharmacy Name}|{pharmacy_name}|{pharmacyName}|{PharmacyName}|{Pharmacy Address}|{pharmacy_address}|{pharmacyAddress}|{Pharmacy Number}|{pharmacy_number}|{pharmacyNumber}|{trial_name}|{Trial Name}|{Trial Provider}|{trial_provider}|{trialProvider}/g,
      chars: 0,
      messageParts: 1,
      perMessageCostFraction: 0,
      displayWarningMessageModal: false,
    }
  },
  computed: {
    inputModel() {
      let value = this.modelValue;
      // Replace new line with break tag
      value = value.replace(/\n/g, '<br>');
      value = value.replace(this.nameHighlightRegex, '<span style="color:#0870DC;">$&</span>');
      value = value.replace(this.nameHighlightRegex, '<mark style="background-color: #E5F1FE;">$&</mark>');
      value = value.replace(this.pharmacyHighlightRegex, '<span style="color:#00AD50;">$&</span>');
      value = value.replace(this.pharmacyHighlightRegex, '<mark style="background-color:#E0FFEE;">$&</mark>');
      value = this.cleanMessageBody(value);
      return value;
    }
  },
  watch: {
    // Watch the modelValue and update the editor content if it changes
    modelValue(value) {
      if (this.editor === null) {
        return;
      }

      let anchorPosition = this.editor.state.selection.$anchor.pos;

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)
      // Replace new line with break tag
      value = this.inputModel;

      let content = value.replace(/(?:<\/p><p>|<br>|<br \\>)/g, '\n');
      // Remove all other HTML tags
      content = content.replace(/<\/?[^>]+(>|$)/g, "");
      // Clean the message body
      content = this.cleanMessageBody(content);
      this.chars = content.length;

      // HTML
      const isSame = this.editor.getHTML() === value

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value, false, {preserveWhitespace: "full"})
      this.editor.commands.focus(anchorPosition);

    },
    maxChars(value) {
      this.messageParts = Math.ceil(this.messageLength / value);
    },
    messageLength(value) {
      this.messageParts = Math.ceil(value / this.maxChars);
    }
  },
  mounted() {
    // Set the per message cost
      this.perMessageCostFraction = this.perMessageCost * 100;

    // Initial char counts
    this.chars = this.modelValue.length;
    this.messageParts = Math.ceil(this.messageLength / this.maxChars);

    // Extend the highlight module to add custom input and paste rules
    const CustomHighlight = Highlight.extend({
      addPasteRules() {
        return [
          markPasteRule({
            find: /{First Name}|{Last Name}|{Pharmacy Name}|{Pharmacy Address}|{Pharmacy Number}/g,
            type: this.type,
          }),
        ]
      }
    });
    // Import the tiptap editor with extensions
    this.editor = new Editor({
      content: this.inputModel,
      extensions: [
        Document,
        Paragraph,
        Text,
        Color,
        TextStyle,
        HardBreak,
        History,
        CustomHighlight.configure({
          inclusive: false,
          exitable: true,
          multicolor: true
        })
      ],
      editable: this.editable,
      onUpdate: () => {
        this.$emit('update:modelValue', this.editor.getHTML());
      }
    })
  },
  beforeUnmount() {
    this.editor.destroy();
  },
  methods: {
    // Insert text into the editor
    insertText(text) {
      this.editor.commands.focus();
      this.editor.chain()
        .setMark("highlight")
        .insertContent(text.name)
        .unsetMark("highlight")
        .run();
    },
     /**
     * Open the message calculation note
     */
     openMsgCalcNoteModal() {
      this.displayWarningMessageModal = true;
      this.warningMessageBody = "When you send an SMS message, it's split into parts based on its length:\n\n- Up to 160 characters: This fits in one part using standard characters. \n- Special characters or emojis: Reduces the limit to 70 characters per part. \n- Longer messages: These get split into parts of 153 characters each (or 67 for special characters) as some space is used to reassemble the message. \n\nThe text \"Reply STOP to opt out\" (21 characters) is appended to each message and contributes to the message parts calculation. Placeholder text such as {Pharmacy Name} will also be replaced with the real text prior to the calculation.\n\nThe total cost will be the number of patients contacted, multiplied by the number of message parts, multiplied by 12" + this.$t('currencyFractionSymbol') + " (the current cost per message part)."
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.editor {
  margin: 1em;
  flex: 1;
}

:deep(.tiptap) {
  height: 100%;
}

:deep(.ProseMirror:focus) {
  outline: none;
}

:deep(.tiptap) p {
  margin: 0;
  text-align: left;
  word-break: break-word;
  color: $deepgrey;
  font-size: 1em;
  font-weight: 500;
}

:deep(.tiptap) mark {
    background-color: #FAF594;
    border-radius: 0.4rem;
    box-decoration-break: clone;
    padding: 0.1rem 0.3rem;
}

.message-cost-text {
  margin-top:auto;
  margin-bottom: 1em;
  display: flex; 
  flex-direction: row;
  color: $deepgrey;
}

.grey-icon {
    min-width: 20px;
    background-color: $deepgrey;
    border-color: $error;
    color: white;
    border-radius: 50% !important;
    height: 20px;
    width: 20px;
    margin-right: 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

</style>